import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Info, Trash2, ExternalLink } from 'lucide-react';
import { MdOutlineWebhook } from 'react-icons/md';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { DBWebhooksThunks, DBUserThunks, webhooksSelectByQuery } from '@common-reducers/DBServiceThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectUserObject } from '@common/reducers/UserSelectors';
import { setUser } from '@/common/reducers/UserReducer';
import { appConfig } from '@common/config/app.config';
import { namespace } from '@common/config/constants';
import {
  isEventEnabled,
  toggleEvent,
  WebhookEventName,
  WEBHOOK_EVENT_GROUPS,
  WEBHOOK_EVENT_LABELS,
} from '@common-models/webhook';

/** The account-level boolean preferences this panel owns. */
type UserPreferenceFlags = {
  mqttIncludeOwnMessages?: boolean;
  mqttIncludeInboundMessages?: boolean;
};

export function WebhooksPanel() {
  const { t, i18n } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const webhookList = useAppSelector((state) =>
    webhooksSelectByQuery(state.DBWebhooksReducer, { deleted: false }),
  );
  const user = useAppSelector(selectUserObject);
  const isRtl = i18n.dir(i18n.language) === 'rtl';
  const fieldDirection = isRtl ? 'rtl' : 'ltr';

  const [webhookUrl, setWebhookUrl] = useState('');

  // ── Account-level MQTT opt-ins ─────────────────────────────────────────
  // NOT per-webhook, so they live outside the per-URL event grids below. Both
  // are off by default (bt-common wa-event-relevance.ts holds the gate they
  // feed).
  //
  // `UserReducer.user` is the ONE source of truth here, for two reasons:
  //  1. selectCurrentUser returns it verbatim when populated (which it is for
  //     any logged-in session) and never consults DBUserReducer — so a
  //     DBUserThunks.patch alone leaves this panel reading the pre-toggle
  //     value, and the SECOND toggle then spreads that stale `preferences`
  //     and silently reverts the first one on the server.
  //  2. WaEventDispatchThunk reads the very same slice to decide whether to
  //     send a fromMe event upstream at all. Without the write-back the
  //     preference does not take effect until a full reload.
  // Hence `setUser(res.payload)` after every patch — the account-view.tsx
  // pattern. Local state exists only to make the switch move on click instead
  // of after the round-trip.
  // Debug-only visibility for the two MQTT switches below. Read once at mount
  // (localStorage can throw in a restricted context, hence the try/catch) —
  // same shape as account-backup.tsx, which gates its own unfinished controls
  // this way. Absent/unreadable → hidden, which is the safe direction.
  const btDebug = (() => {
    try { return localStorage.getItem('bt-debug') === 'true'; } catch { return false; }
  })();

  const [pendingPrefs, setPendingPrefs] = useState<Partial<UserPreferenceFlags>>({});
  const prefValue = (key: keyof UserPreferenceFlags): boolean =>
    pendingPrefs[key] ?? (user?.preferences as UserPreferenceFlags | undefined)?.[key] === true;

  const togglePref = async (key: keyof UserPreferenceFlags) => {
    if (!user?._id) return;
    const next = !prefValue(key);
    setPendingPrefs((prev) => ({ ...prev, [key]: next }));
    // `pendingPrefs` is layered OVER `user.preferences` on purpose. The store
    // slice only catches up when a patch RESOLVES, so with two switches the
    // second click still reads the pre-first-toggle `preferences` and ships it
    // — silently reverting the first toggle on the server, which is the very
    // race the note above describes. Local pending state is the newer truth for
    // any key still in flight, so it wins; everything else comes from the
    // server's own last word.
    const res: any = await dispatch(
      DBUserThunks.patch({
        entity: {
          _id: user._id,
          preferences: { ...user.preferences, ...pendingPrefs, [key]: next },
        },
      }),
    );
    if (res?.payload) dispatch(setUser(res.payload));
    // Hand authority back to the store — a rejected patch must not leave the
    // switch showing a value the server never accepted.
    setPendingPrefs((prev) => {
      const { [key]: _dropped, ...rest } = prev;
      return rest;
    });
  };

  useEffect(() => {
    dispatch(DBWebhooksThunks.find({ deleted: false }));
  }, [dispatch]);

  const addWebhook = async () => {
    if (!webhookUrl.trim()) return;
    dispatch(DBWebhooksThunks.create({ url: webhookUrl }));
    setWebhookUrl('');
  };

  // All mutations send a FIELD-SCOPED patch ({ _id, <changedField> }) rather
  // than the whole document. The DB thunk merges this into the store (RTK
  // updateOne) and ships only the delta to the server, so flipping one switch
  // no longer re-sends url/owner/enabled/events/consecutiveFailures — smaller
  // payload, and it can't clobber a field changed concurrently elsewhere.
  const deleteHook = (hook: any) => {
    dispatch(
      DBWebhooksThunks.patch({
        entity: { _id: hook._id, deleted: true },
      }),
    );
  };

  const toggleWebhookEvent = (webhook: any, eventName: WebhookEventName) => {
    const nextValue = !isEventEnabled(webhook, eventName);
    dispatch(
      DBWebhooksThunks.patch({
        entity: {
          _id: webhook._id,
          events: toggleEvent(webhook.events, eventName, nextValue),
        },
      }),
    );
  };

  // A webhook is active unless explicitly disabled (enabled === false).
  const isWebhookEnabled = (webhook: any) => webhook.enabled !== false;

  const toggleWebhookEnabled = (webhook: any) => {
    dispatch(
      DBWebhooksThunks.patch({
        entity: { _id: webhook._id, enabled: !isWebhookEnabled(webhook) },
      }),
    );
  };

  return (
    <div className="space-y-5">
      {/* Combined info bar */}
      <Alert className="bg-[var(--color-surface)] border-[var(--color-border)]">
        <AlertDescription className="flex items-start gap-2 text-[var(--color-text)]">
          <Info className="h-4 w-4 shrink-0 text-[var(--status-info)] mt-0.5" />
          <span>
            <Trans
              t={t}
              i18nKey="api.webhooksInfoBar"
              components={{
                make: (
                  <a
                    href={`${appConfig.PRICING_PAGE}/webhook-make-guide`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-[var(--status-info)] hover:underline inline-flex items-center gap-1"
                  >
                    Make
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ),
                zapier: (
                  <a
                    href={`${appConfig.PRICING_PAGE}/webhook-zapier-guide`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-[var(--status-info)] hover:underline inline-flex items-center gap-1"
                  >
                    Zapier
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ),
              }}
            />
          </span>
        </AlertDescription>
      </Alert>

      {/* Both MQTT opt-ins are DEBUG-ONLY for now (localStorage['bt-debug']==='true'),
          matching how account-backup.tsx hides its unfinished controls.
          They are shipping ahead of the gateway release that makes them take
          effect on the production WhatsApp fleet, so a regular user could turn
          one on and see nothing happen. The preferences themselves default OFF
          server-side, so hiding the switches is what keeps them unreachable —
          nothing else can set them. Remove this gate once the gateway ships. */}
      {btDebug && (
      <>
      {/* Account-level MQTT opt-in for own (fromMe) messages */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-[var(--color-text)]">
            {t('api.mqttIncludeOwnMessagesLabel', 'Include my own sent messages in MQTT events')}
          </p>
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
            {t(
              'api.mqttIncludeOwnMessagesHint',
              'Off by default. When on, messages you send yourself from your phone also publish to MQTT — independent of the webhook toggle above.',
            )}
          </p>
        </div>
        <Switch
          checked={prefValue('mqttIncludeOwnMessages')}
          onCheckedChange={() => togglePref('mqttIncludeOwnMessages')}
          className="data-[state=checked]:bg-[var(--color-accent)] shrink-0"
          aria-label={t('api.mqttIncludeOwnMessagesLabel', 'Include my own sent messages in MQTT events')}
        />
      </div>

      {/* Account-level MQTT opt-in for inbound (customer) messages — makes
          MQTT a fully independent channel, not merely a side effect of
          having a subscribed webhook. */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-[var(--color-text)]">
            {t('api.mqttIncludeInboundMessagesLabel', 'Include incoming messages in MQTT events')}
          </p>
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
            {t(
              'api.mqttIncludeInboundMessagesHint',
              'Off by default. When on, incoming customer messages publish to MQTT even with no webhook configured.',
            )}
          </p>
        </div>
        <Switch
          checked={prefValue('mqttIncludeInboundMessages')}
          onCheckedChange={() => togglePref('mqttIncludeInboundMessages')}
          className="data-[state=checked]:bg-[var(--color-accent)] shrink-0"
          aria-label={t('api.mqttIncludeInboundMessagesLabel', 'Include incoming messages in MQTT events')}
        />
      </div>
      </>
      )}

      {/* Add webhook row */}
      <div className="flex gap-2 items-end" style={{ direction: fieldDirection }}>
        <div className="flex-1 space-y-2 min-w-0">
          <Label htmlFor="webhook-url">{t('api.webhooksAddressLabel')}</Label>
          <Input
            id="webhook-url"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addWebhook();
              }
            }}
            dir={fieldDirection}
          />
        </div>
        <Button
          onClick={addWebhook}
          disabled={!webhookUrl.trim()}
          className="bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent)]/90 border-transparent"
        >
          {t('api.webhooksAddButton')}
        </Button>
      </div>

      {/* Webhook list */}
      {webhookList.length === 0 ? (
        <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-8 text-center text-sm text-[var(--color-text-muted)]">
          {t('api.webhooksNoWebhooks')}
        </div>
      ) : (
        <div className="space-y-3">
          {webhookList.map((webhook: any) => (
            <div
              key={webhook._id}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-4 space-y-4"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-surface-accent)] text-[var(--color-text)] shrink-0">
                  <MdOutlineWebhook className="h-4 w-4" />
                </div>
                <p
                  className="flex-1 min-w-0 text-sm font-medium truncate text-[var(--color-text)]"
                  title={webhook.url}
                >
                  {webhook.url}
                </p>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-[var(--color-text-muted)] hidden sm:inline">
                    {isWebhookEnabled(webhook)
                      ? t('api.webhookStatusEnabled', 'Enabled')
                      : t('api.webhookStatusDisabled', 'Disabled')}
                  </span>
                  <Switch
                    checked={isWebhookEnabled(webhook)}
                    onCheckedChange={() => toggleWebhookEnabled(webhook)}
                    className="data-[state=checked]:bg-[var(--color-accent)]"
                    aria-label={t('api.webhookToggleEnabledAriaLabel', 'Enable or disable this webhook')}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteHook(webhook)}
                  className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                  aria-label={t('api.webhookDeleteAriaLabel')}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-3">
                {WEBHOOK_EVENT_GROUPS.map((group) => (
                  <div key={group.key} className="space-y-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)] px-1">
                      {t(group.labelKey)}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                      {group.events.map((eventName) => (
                        <label
                          key={eventName}
                          className="flex items-center justify-between gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 cursor-pointer hover:bg-[var(--color-surface-muted)] transition-colors"
                        >
                          <span className="text-xs text-[var(--color-text)] truncate">
                            {WEBHOOK_EVENT_LABELS[eventName]}
                          </span>
                          <Switch
                            checked={isEventEnabled(webhook, eventName)}
                            onCheckedChange={() => toggleWebhookEvent(webhook, eventName)}
                            className="data-[state=checked]:bg-[var(--color-accent)]"
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default WebhooksPanel;
